import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Compass, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1462396240927-52058a6a84ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxnbGFzcyUyMGNvcnBvcmF0ZSUyMGJ1aWxkaW5nJTIwdXB3YXJkJTIwYW5nbGV8ZW58MHx8fHwxNzgyODc1MTk4fDA&ixlib=rb-4.1.0&q=85";
const OFFICE_IMG = "https://images.unsplash.com/photo-1575318633968-0383e7d07ca0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBkZXNrJTIwZG9jdW1lbnR8ZW58MHx8fHwxNzgyODc1MTk4fDA&ixlib=rb-4.1.0&q=85";
const HANDSHAKE_IMG = "https://images.unsplash.com/photo-1549923746-c502d488b3ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxzdWNjZXNzJTIwaGFuZHNoYWtlJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzgyODc1MTk4fDA&ixlib=rb-4.1.0&q=85";
const SCALES_IMG = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxsYXclMjBzY2FsZXMlMjBvZiUyMGp1c3RpY2UlMjBtaW5pbWFsfGVufDB8fHx8MTc4Mjg3NTIwN3ww&ixlib=rb-4.1.0&q=85";

const PRACTICE = [
  {
    code: "EB-1A",
    title: "Extraordinary Ability",
    body: "Green card for individuals at the very top of their field — scientists, executives, artists, athletes. No employer sponsorship required.",
    icon: Award,
  },
  {
    code: "EB-2 NIW",
    title: "National Interest Waiver",
    body: "Self-petition green card for advanced-degree professionals whose work meaningfully benefits the United States.",
    icon: Compass,
  },
  {
    code: "O-1",
    title: "Extraordinary Ability Visa",
    body: "Non-immigrant visa for individuals with sustained national or international acclaim. Faster path — often paired with EB-1A later.",
    icon: ShieldCheck,
  },
];

export default function Home() {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/success-stories", { params: { featured: true } })
      .then((r) => setStories(r.data.slice(0, 3)))
      .catch(() => setStories([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-x pt-28 pb-40 lg:pt-40 lg:pb-56">
          <div className="max-w-4xl">
            <div className="eyebrow fade-up">EB-1A · EB-2 NIW · O-1</div>
            <h1 className="fade-up d-100 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[#0F172A] mt-6">
              A U.S. green card for those who have already
              <span className="italic text-[#C2A679]"> made their mark.</span>
            </h1>
            <p className="fade-up d-200 mt-8 max-w-2xl text-lg text-[#4A4D50] leading-relaxed">
              We build employment-based petitions for researchers, founders, and creatives.
              No volume mill. Every case drafted, reviewed, and argued by an attorney.
            </p>
            <div className="fade-up d-300 mt-10 flex flex-wrap gap-4">
              <Link to="/case-review" data-testid="hero-cta-primary" className="btn-primary">
                Request Initial Case Review <ArrowUpRight size={18} />
              </Link>
              <Link to="/practice-areas" data-testid="hero-cta-secondary" className="btn-secondary">
                Explore Practice Areas
              </Link>
            </div>
            <div className="fade-up d-400 mt-16 grid grid-cols-3 gap-8 max-w-2xl">
              {[["94%", "EB-1A approval rate"], ["300+", "Petitions filed"], ["40+", "Countries served"]].map(([n, l]) => (
                <div key={l} className="border-l border-[#0F172A]/20 pl-4">
                  <div className="font-display text-3xl text-[#0F172A]">{n}</div>
                  <div className="mt-2 text-xs uppercase tracking-widest text-[#4A4D50]">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRACTICE STRIP */}
      <section className="container-x py-24 lg:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-5">
            <div className="eyebrow">01 — Practice</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 text-[#0F172A] leading-tight tracking-tight">
              We do three things.
              <br />We do them exceptionally.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-[#4A4D50] leading-relaxed">
            Employment-based immigration law rewards specialization. By focusing on the highest-scrutiny
            categories, we know what USCIS adjudicators want to see, and — just as important — what causes
            them to hesitate.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {PRACTICE.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.code} data-testid={`practice-card-${p.code}`} className="card-editorial p-10 relative">
                <Icon size={28} className="text-[#C2A679]" strokeWidth={1.5} />
                <div className="mt-6 font-mono-plex text-xs tracking-[0.2em] text-[#4A4D50]">
                  {String(i + 1).padStart(2, "0")} / {p.code}
                </div>
                <h3 className="font-display text-2xl mt-2 text-[#0F172A]">{p.title}</h3>
                <p className="mt-4 text-[#4A4D50] leading-relaxed">{p.body}</p>
                <Link to="/practice-areas" className="mt-8 inline-flex items-center gap-2 text-sm font-medium link-underline text-[#0F172A]">
                  Learn more <ArrowUpRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="bg-[#EBE8DF]/60 py-24 lg:py-32 border-t border-b border-[#0F172A]/10">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 relative">
            <img src={OFFICE_IMG} alt="Office" className="w-full h-[520px] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-[#0F172A] text-[#F7F5F0] p-8 max-w-xs hidden md:block">
              <div className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-[#C2A679]">Approach</div>
              <div className="font-display text-2xl mt-2 leading-tight">Every petition drafted, argued, and defended by counsel — not paralegals.</div>
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="eyebrow">02 — Process</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight text-[#0F172A]">
              A boutique standard for a life-shaping filing.
            </h2>
            <div className="mt-12 space-y-8">
              {[
                ["Case Review", "You submit background, evidence, and a $200 case review fee. We return a written strategy within 5 business days."],
                ["Engagement", "If we move forward, a fixed-fee engagement letter is signed. No hourly ambiguity."],
                ["Evidence & Drafting", "We build the exhibits, draft expert opinion letters, and iterate the petition until it's undeniable."],
                ["Filing & Response", "We file, respond to any RFEs at no additional charge, and stay engaged through adjudication."],
              ].map(([title, body], i) => (
                <div key={title} className="grid grid-cols-12 gap-4 border-t border-[#0F172A]/15 pt-6">
                  <div className="col-span-2 font-mono-plex text-sm text-[#C2A679]">0{i + 1}</div>
                  <div className="col-span-10">
                    <div className="font-display text-xl text-[#0F172A]">{title}</div>
                    <p className="mt-2 text-[#4A4D50] leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES PREVIEW */}
      <section className="container-x py-24 lg:py-32">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="eyebrow">03 — Results</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 text-[#0F172A] leading-tight tracking-tight">
              Selected approvals.
            </h2>
          </div>
          <Link to="/success-stories" data-testid="home-see-all-stories" className="text-sm link-underline text-[#0F172A]">
            See all success stories →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {(stories.length > 0 ? stories : demoStories).map((s, idx) => (
            <article key={s.id || idx} data-testid={`home-story-${idx}`} className="card-editorial p-8 flex flex-col">
              <div className="flex items-center justify-between">
                <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#C2A679]">{s.category}</span>
                <span className="text-xs text-[#4A4D50]">{s.outcome_year || "—"}</span>
              </div>
              <h3 className="font-display text-xl mt-6 text-[#0F172A] leading-snug">{s.title}</h3>
              <div className="mt-3 text-sm text-[#4A4D50]">{s.client_field}{s.country_of_origin ? ` · ${s.country_of_origin}` : ""}</div>
              <p className="mt-6 text-sm text-[#4A4D50] leading-relaxed flex-1">{s.summary}</p>
              <div className="mt-8 pt-6 border-t border-[#0F172A]/10 text-xs eyebrow text-[#1A3626]">Approved</div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative overflow-hidden bg-[#0F172A] text-[#F7F5F0]">
        <img src={SCALES_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-luminosity" />
        <div className="relative container-x py-24 lg:py-32 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow text-[#C2A679]">Ready when you are</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight tracking-tight">
              A written case strategy — for $200.
            </h2>
            <p className="mt-6 max-w-2xl text-lg text-[#F7F5F0]/70 leading-relaxed">
              Share your CV, publications, and a short summary of your work. Within five business days,
              you'll receive a candid, written assessment of your best-fit category and what a winning file would require.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/case-review" data-testid="banner-cta" className="btn-primary !bg-[#C2A679] !border-[#C2A679] !text-[#0F172A] hover:!bg-[#D9C7A3]">
              Begin Case Review <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Fallback preview when no admin-loaded stories yet
const demoStories = [
  { category: "EB-1A", title: "AI researcher approved without RFE", client_field: "Machine Learning", country_of_origin: "India", outcome_year: 2025, summary: "Principal researcher at a major lab; 40+ publications, 3,000+ citations. Approved in 12 days via premium processing." },
  { category: "EB-2 NIW", title: "Climate scientist self-petition", client_field: "Atmospheric Physics", country_of_origin: "Brazil", outcome_year: 2024, summary: "PhD conducting IPCC-adjacent research. NIW approved on first filing with a modest publication record." },
  { category: "O-1", title: "Choreographer for national ballet", client_field: "Performing Arts", country_of_origin: "Ukraine", outcome_year: 2025, summary: "Featured artist with national press coverage. O-1B approved for a three-year initial term." },
];
